import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useUrlFilters } from "@/lib/urlfilters";
import { todayStr } from "@/lib/format";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Crosshair, Warning } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const EMP_STATUSES = ["active", "probationary", "suspended", "separated"];
const CIVIL_STATUS = ["single", "married", "widowed", "separated", "annulled"];

//: The four numbers a remittance file is keyed on, each with the shape its own
//: agency prints on the card. A placeholder is the cheapest way to stop an SSS
//: number being typed into the Pag-IBIG box.
const GOV_IDS = [
  ["sss_no", "SSS number", "02-1234567-8"],
  ["philhealth_no", "PhilHealth number", "12-345678901-2"],
  ["pagibig_no", "Pag-IBIG MID", "1234-5678-9012"],
  ["tin", "TIN", "123-456-789-000"],
];

//: Blur-to-save. Returns null for an emptied field rather than "", so the
//: backend stores an absence instead of a blank string that later reads as a
//: value somebody entered.
const PARTICULARS = [
  ["date_of_birth", "Date of birth", "date"],
  ["date_hired", "Date hired", "date"],
  ["dependents", "Dependents", "number"],
  ["emergency_contact_name", "Emergency contact", "text"],
  ["emergency_contact_phone", "Their number", "tel"],
  ["emergency_contact_relation", "Relationship", "text"],
];
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
//: The ranks this agency actually uses. Derived from the guards on file and
//: unioned with the standard set, never a fixed list on its own -- a dropdown
//: narrower than the data rewrites whatever it cannot represent.
const BASE_RANKS = ["Detachment Commander", "Shift Supervisor", "Senior Security Guard", "Security Guard"];

export default function Guards() {
  const [guards, setGuards] = useState([]);
  // From the dashboard: ?view=deployable and ?view=expiring.
  const [f, setF] = useUrlFilters({ view: "all" });
  const [branches, setBranches] = useState([]);
  const [config, setConfig] = useState(null);
  const [sel, setSel] = useState(null);
  const [leaves, setLeaves] = useState([]);
  //: Read from the ARMOURY, never stored on the guard. One custody chain, read
  //: from the object it belongs to, so a return recorded at the armoury cannot
  //: disagree with what this file says.
  const [firearm, setFirearm] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", branch_id: "", phone: "", address: "", position: "", daily_rate: "" });
  const [docForm, setDocForm] = useState({ type: "", number: "", expiry: "", required: true });
  const [leaveForm, setLeaveForm] = useState({ from_date: "", to_date: "", type: "vacation", reason: "" });

  //: Union of the standard ranks and every rank already on a guard, so an
  //: agency that uses its own titles keeps them instead of losing them to a
  //: list that predates their data.
  const ranks = Array.from(new Set([...BASE_RANKS, ...guards.map((g) => g.position).filter(Boolean)])).sort();

  const load = () => api.get("/guards").then((r) => setGuards(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const pick = async (g) => {
    setSel(g);
    setFirearm(null);
    const { data } = await api.get(`/leaves?guard_id=${g.id}`);
    setLeaves(data);
    api.get(`/guards/${g.id}/firearms`).then((r) => setFirearm(r.data)).catch(() => setFirearm(null));
  };

  const refreshSel = async (g) => {
    const { data } = await api.get("/guards");
    setGuards(data);
    const fresh = data.find((x) => x.id === g.id);
    if (fresh) setSel(fresh);
  };

  //: Blank rate -> null, NOT 0. Null means "fall back to the contract rate",
  //: which is what the backend documents and what every existing guard relies
  //: on. A stored 0 is a real number that merely looks like absence.
  const rateOrNull = (v) => {
    const t = String(v ?? "").trim();
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  const createGuard = async () => {
    const rate = rateOrNull(form.daily_rate);
    if (String(form.daily_rate).trim() && rate === null) {
      toast.error("Daily rate must be a number greater than zero, or left blank");
      return;
    }
    try {
      await api.post("/guards", { ...form, position: form.position || null, daily_rate: rate });
      toast.success("Guard profile created");
      setNewOpen(false);
      setForm({ first_name: "", last_name: "", branch_id: "", phone: "", address: "", position: "", daily_rate: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const patch = async (fields, msg) => {
    try {
      await api.patch(`/guards/${sel.id}`, fields);
      toast.success(msg);
      refreshSel(sel);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addDoc = async () => {
    try {
      await api.post(`/guards/${sel.id}/documents`, { ...docForm, expiry: docForm.expiry || null });
      toast.success("Document added");
      setDocForm({ type: "", number: "", expiry: "", required: true });
      refreshSel(sel);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addLeave = async () => {
    try {
      await api.post(`/guards/${sel.id}/leaves`, leaveForm);
      toast.success("Leave filed");
      setLeaveForm({ from_date: "", to_date: "", type: "vacation", reason: "" });
      pick(sel);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const expSoon = (g) => {
    const today = todayStr();
    return (g.documents || []).filter((d) => d.expiry && d.expiry <= today).length;
  };

  const shownGuards = guards.filter((g) => (

    f.view === "deployable" ? g.deployable && g.employment_status === "active"

      : f.view === "not_deployable" ? !(g.deployable && g.employment_status === "active")

        : f.view === "expiring" ? (g.expired_mandatory_docs || 0) > 0

          || (g.documents || []).some((d) => d.required && d.expiry

            && d.expiry <= new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10))

          : true));


  return (
    <div data-testid="guards-page">
      <PageHeader title="Guards & Workforce" sub="Profiles, licenses & expiry blocking, availability and leave"
        actions={<Button data-testid="new-guard-button" className="rounded-none" onClick={() => setNewOpen(true)}>New guard</Button>} />
            <div className="flex flex-wrap gap-2 mb-3 items-end" data-testid="guard-filters">
        <div className="w-64">
          <Label className="text-[10px] uppercase font-bold">View</Label>
          <Select value={f.view} onValueChange={(v) => setF({ view: v })}>
            <SelectTrigger data-testid="guard-view-filter" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Everybody on the books</SelectItem>
              <SelectItem value="deployable">Deployable (can be rostered)</SelectItem>
              <SelectItem value="not_deployable">Not deployable</SelectItem>
              <SelectItem value="expiring">Mandatory document expiring or expired</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <DataTable testid="guards-table" onRowClick={pick} rows={shownGuards}
        columns={[
          { key: "employee_no", label: "Emp No", render: (r) => <span className="font-mono text-xs">{r.employee_no}</span> },
          { key: "name", label: "Guard", render: (r) => <div><div className="font-semibold">{r.name}</div><div className="font-mono text-[10px] text-muted-foreground">{r.phone}</div></div> },
          { key: "position", label: "Rank / rate", render: (r) => (
            <div>
              <div className="text-xs">{r.position || <span className="text-muted-foreground">—</span>}</div>
              <div className="font-mono text-[10px] text-muted-foreground">
                {r.daily_rate
                  ? `₱${Number(r.daily_rate).toLocaleString("en-PH", { minimumFractionDigits: 2 })}/day`
                  : "contract rate"}
              </div>
            </div>) },
          { key: "branch_name", label: "Branch" },
          { key: "employment_status", label: "Employment", render: (r) => <StatusBadge value={r.employment_status} /> },
          { key: "deployable", label: "Deployable", render: (r) => (r.deployable ? <StatusBadge value="active" /> : <StatusBadge value="missed" testid="non-deployable" />) },
          { key: "docs", label: "Expired mandatory docs", render: (r) => { const n = expSoon(r); return n ? <span className="font-mono text-xs text-[hsl(var(--alert))] font-bold">{n} EXPIRED</span> : <span className="font-mono text-xs text-muted-foreground">0</span>; } },
          { key: "skills", label: "Skills", render: (r) => <span className="text-xs">{(r.skills || []).join(", ") || "—"}</span> },
        ]} />

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="rounded-none" data-testid="new-guard-dialog">
          <DialogHeader><DialogTitle className="font-heading">New guard</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider font-bold">First name</Label><Input data-testid="guard-first-name" className="rounded-none mt-1" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Last name</Label><Input data-testid="guard-last-name" className="rounded-none mt-1" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold">Branch</Label>
              <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                <SelectTrigger data-testid="guard-branch" className="rounded-none mt-1"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Mobile (PH)</Label><Input data-testid="guard-phone" className="rounded-none mt-1" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+63 9xx xxx xxxx" /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Address</Label><Input data-testid="guard-address" className="rounded-none mt-1" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Rank</Label>
              <Select value={form.position || "none"} onValueChange={(v) => setForm({ ...form, position: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="guard-position" className="rounded-none mt-1"><SelectValue placeholder="Select rank" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not set</SelectItem>
                  {ranks.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Daily rate</Label>
              <Input data-testid="guard-daily-rate" type="number" min="1" step="0.01" inputMode="decimal"
                className="rounded-none mt-1 font-mono" placeholder="e.g. 645.00"
                value={form.daily_rate} onChange={(e) => setForm({ ...form, daily_rate: e.target.value })} />
            </div>
            <div className="col-span-2 -mt-1 text-[10px] text-muted-foreground">
              A rate here is what the guard is <span className="font-bold">PAID</span>, by rank.
              Leave it blank and payroll falls back to the client contract rate — which pays a
              commander and a probationary guard the same peso.
            </div>
          </div>
          <Button data-testid="guard-create-submit" className="w-full rounded-none mt-3" onClick={createGuard}>Create guard</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sel} onOpenChange={() => setSel(null)}>
        <DialogContent className="rounded-none max-w-3xl max-h-[85vh] overflow-auto" data-testid="guard-detail-dialog">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{sel.name} <span className="font-mono text-xs text-muted-foreground">{sel.employee_no}</span></DialogTitle>
              </DialogHeader>
              <div className="grid md:grid-cols-3 gap-3 text-sm mb-4">
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Branch</div>{sel.branch_name}</div>
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Phone</div><span className="font-mono text-xs">{sel.phone}</span></div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Employment</div>
                  <Select value={sel.employment_status} onValueChange={(v) => patch({ employment_status: v }, "Status updated")}>
                    <SelectTrigger data-testid="guard-status-select" className="rounded-none mt-1 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>{EMP_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Switch data-testid="guard-deployable-toggle" checked={sel.deployable} onCheckedChange={(v) => patch({ deployable: v }, v ? "Marked deployable" : "Marked non-deployable")} />
                  <Label className="text-xs uppercase tracking-wider font-bold">Deployable</Label>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Rank</div>
                  <Select value={sel.position || "none"} onValueChange={(v) => patch({ position: v === "none" ? null : v }, "Rank updated")}>
                    <SelectTrigger data-testid="guard-position-select" className="rounded-none mt-1 h-8"><SelectValue placeholder="Not set" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not set</SelectItem>
                      {ranks.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Daily rate (pay)</div>
                  <Input
                    data-testid="guard-daily-rate-edit" type="number" min="1" step="0.01" inputMode="decimal"
                    className="rounded-none mt-1 h-8 font-mono" placeholder="contract rate"
                    defaultValue={sel.daily_rate ?? ""}
                    key={`rate-${sel.id}-${sel.daily_rate ?? "none"}`}
                    onBlur={(e) => {
                      const raw = String(e.target.value ?? "").trim();
                      const next = rateOrNull(raw);
                      if (raw && next === null) { toast.error("Daily rate must be greater than zero, or blank"); e.target.value = sel.daily_rate ?? ""; return; }
                      const cur = sel.daily_rate ?? null;
                      if (next === cur) return;
                      patch({ daily_rate: next }, next === null ? "Rate cleared -- contract rate applies" : "Daily rate updated");
                    }} />
                  <div className="text-[10px] text-muted-foreground mt-0.5">Blank = client contract rate</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Weekly rest day</div>
                  <Select value={sel.weekly_rest_day === null || sel.weekly_rest_day === undefined ? "none" : String(sel.weekly_rest_day)} onValueChange={(v) => patch({ weekly_rest_day: v === "none" ? null : parseInt(v) }, "Rest day updated")}>
                    <SelectTrigger data-testid="guard-restday-select" className="rounded-none mt-1 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">None</SelectItem>{DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {/* ---- government numbers -------------------------------------
                  What SSS, PhilHealth, HDMF and the BIR key a remittance on.
                  Payroll computes the contribution correctly without them and
                  the export cannot say whose it is, so a missing one is called
                  out here and again in the file itself. */}
              <h3 className="font-heading font-bold mb-2">Government numbers</h3>
              <div className="grid md:grid-cols-2 gap-3 mb-2" data-testid="guard-gov-ids">
                {GOV_IDS.map(([key, label, ph]) => (
                  <div key={key}>
                    <Label className="text-[10px] uppercase font-bold">{label}</Label>
                    <Input
                      data-testid={"guard-" + key}
                      className="rounded-none mt-1 h-8 font-mono"
                      placeholder={ph}
                      defaultValue={sel[key] || ""}
                      key={key + "-" + sel.id + "-" + (sel[key] || "")}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v === (sel[key] || "")) return;
                        patch({ [key]: v || null }, v ? label + " saved" : label + " cleared");
                      }} />
                  </div>
                ))}
              </div>
              {GOV_IDS.some(([k]) => !sel[k]) && (
                <div className="text-[11px] text-[hsl(var(--warning))] mb-5 font-semibold" data-testid="guard-gov-missing">
                  {GOV_IDS.filter(([k]) => !sel[k]).length} number(s) missing — this guard&apos;s row in the
                  remittance file will read <span className="font-mono">** MISSING **</span> instead of an
                  account the agency can credit.
                </div>
              )}

              {/* ---- particulars --------------------------------------------- */}
              <h3 className="font-heading font-bold mb-2">Personal particulars</h3>
              <div className="grid md:grid-cols-3 gap-3 mb-5" data-testid="guard-particulars">
                {PARTICULARS.map(([key, label, type]) => (
                  <div key={key}>
                    <Label className="text-[10px] uppercase font-bold">{label}</Label>
                    <Input
                      data-testid={"guard-" + key} type={type}
                      className={"rounded-none mt-1 h-8 " + (type === "date" || type === "number" ? "font-mono" : "")}
                      defaultValue={sel[key] ?? ""}
                      key={key + "-" + sel.id + "-" + (sel[key] ?? "")}
                      onBlur={(e) => {
                        const raw = e.target.value.trim();
                        const v = type === "number" ? (raw === "" ? null : Number(raw)) : (raw || null);
                        if ((v ?? "") === (sel[key] ?? "")) return;
                        patch({ [key]: v }, label + " saved");
                      }} />
                  </div>
                ))}
                <div>
                  <Label className="text-[10px] uppercase font-bold">Civil status</Label>
                  <Select value={sel.civil_status || "none"}
                          onValueChange={(v) => patch({ civil_status: v === "none" ? null : v }, "Civil status saved")}>
                    <SelectTrigger data-testid="guard-civil-status" className="rounded-none mt-1 h-8"><SelectValue placeholder="Not set" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not set</SelectItem>
                      {CIVIL_STATUS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {sel.employment_status === "separated" && (
                  <>
                    <div>
                      <Label className="text-[10px] uppercase font-bold">Date separated</Label>
                      <Input
                        data-testid="guard-date-separated" type="date"
                        className="rounded-none mt-1 h-8 font-mono"
                        defaultValue={sel.date_separated || ""}
                        key={"sep-" + sel.id + "-" + (sel.date_separated || "")}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v === (sel.date_separated || "")) return;
                          patch({ date_separated: v || null }, "Separation date saved");
                        }} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase font-bold">Reason</Label>
                      <Input
                        data-testid="guard-separation-reason"
                        className="rounded-none mt-1 h-8"
                        defaultValue={sel.separation_reason || ""}
                        key={"sr-" + sel.id + "-" + (sel.separation_reason || "")}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v === (sel.separation_reason || "")) return;
                          patch({ separation_reason: v || null }, "Separation reason saved");
                        }} />
                    </div>
                  </>
                )}
              </div>

              <h3 className="font-heading font-bold mb-2">Documents & licenses</h3>
              <DataTable testid="guard-docs-table" rows={sel.documents || []} empty="No documents on file"
                columns={[
                  { key: "type", label: "Type", render: (r) => <span className="font-semibold">{r.type}</span> },
                  { key: "number", label: "Number", render: (r) => <span className="font-mono text-xs">{r.number}</span> },
                  { key: "expiry", label: "Expiry", render: (r) => {
                      const expired = r.expiry && r.expiry < todayStr();
                      return <span className={`font-mono text-xs ${expired ? "text-[hsl(var(--alert))] font-bold" : ""}`}>{r.expiry || "—"}{expired ? " EXPIRED" : ""}</span>;
                    } },
                  { key: "required", label: "Mandatory", render: (r) => (r.required ? "Yes — blocks assignment" : "No") },
                ]} />
              <div className="grid grid-cols-4 gap-2 mt-2 items-end">
                <div>
                  <Label className="text-[10px] uppercase font-bold">Type</Label>
                  <Select value={docForm.type} onValueChange={(v) => setDocForm({ ...docForm, type: v })}>
                    <SelectTrigger data-testid="doc-type" className="rounded-none mt-1 h-8"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{(config?.document_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-[10px] uppercase font-bold">Number</Label><Input data-testid="doc-number" className="rounded-none mt-1 h-8" value={docForm.number} onChange={(e) => setDocForm({ ...docForm, number: e.target.value })} /></div>
                <div><Label className="text-[10px] uppercase font-bold">Expiry</Label><Input data-testid="doc-expiry" type="date" className="rounded-none mt-1 h-8" value={docForm.expiry} onChange={(e) => setDocForm({ ...docForm, expiry: e.target.value })} /></div>
                <Button size="sm" data-testid="doc-add" className="rounded-none" onClick={addDoc} disabled={!docForm.type}>Add document</Button>
              </div>

              {/* ---- firearm --------------------------------------------------
                  Read from the ARMOURY, not from this record. Under RA 10591 the
                  firearm is on the AGENCY licence -- so it is an agency asset with
                  a custody chain, and `gun_serial` as a field on the guard would
                  leave nobody able to say which serial walked out on the day they
                  resign, whether the rounds came back, or who signed for it. */}
              <h3 className="font-heading font-bold mt-5 mb-2 flex items-center gap-2">
                <Crosshair size={18} /> Firearm &amp; ammunition
              </h3>
              {firearm?.current ? (
                <div className="border border-[hsl(var(--warning))] bg-card p-3 mb-2" data-testid="guard-firearm-current">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-heading font-bold text-base">
                        {firearm.current.make} {firearm.current.model}
                      </div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wider">
                        {firearm.current.firearm_type} · {firearm.current.caliber}
                      </div>
                    </div>
                    <StatusBadge value="issued" />
                  </div>
                  <div className="grid md:grid-cols-4 gap-3 mt-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Serial number</div>
                      <div className="font-mono text-sm font-bold" data-testid="guard-firearm-serial">{firearm.current.serial_no}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Rounds issued</div>
                      <div className="font-mono text-sm font-bold" data-testid="guard-firearm-rounds">{firearm.current.rounds_out}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Registration</div>
                      <div className="font-mono text-[11px]">{firearm.current.license_no || "—"}</div>
                      <div className="font-mono text-[10px] text-muted-foreground">
                        expires {firearm.current.license_expiry || "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Duty detail</div>
                      <div className="font-mono text-[11px]">{firearm.current.duty_detail_no || "—"}</div>
                    </div>
                  </div>
                  <div className="mt-3 border-t border-border pt-2 flex items-start gap-2">
                    <Warning size={15} weight="fill" className="text-[hsl(var(--warning))] shrink-0 mt-0.5" />
                    <div className="text-[11px] leading-snug">
                      <span className="font-bold">This guard cannot be offboarded while this is out.</span>{" "}
                      Setting employment to separated or resigned is refused until the unit and its
                      rounds are receipted back. Do that on the{" "}
                      <Link to="/firearms" className="underline font-semibold">Armoury</Link> screen.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border border-border border-dashed p-3 mb-2 text-xs text-muted-foreground"
                     data-testid="guard-firearm-none">
                  Not signed for a firearm. Nothing blocks offboarding on this side.{" "}
                  <Link to="/firearms" className="underline">Issue one from the Armoury</Link> if this
                  guard is posted armed.
                </div>
              )}

              {firearm?.history?.length > 0 && (
                <>
                  <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground mt-3 mb-1">
                    Custody history
                  </div>
                  <div data-testid="guard-firearm-history">
                    {firearm.history.map((e) => (
                      <div key={e.id} className="border-t border-border py-1.5 text-[11px]">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono">
                            {e.serial_no} · {e.make} {e.model} · {e.caliber}
                          </span>
                          <StatusBadge value={e.returned_at ? "returned" : "out"} />
                        </div>
                        <div className="text-muted-foreground">
                          Issued {e.issued_on} by {e.issued_by} with {e.rounds_issued} round(s)
                          {e.returned_on
                            ? ` · returned ${e.returned_on} to ${e.received_by}, ${e.rounds_returned} round(s) back`
                            : " · still out"}
                        </div>
                        {e.rounds_unaccounted > 0 && (
                          <div className="border-l-2 border-[hsl(var(--alert))] pl-2 mt-1">
                            <span className="font-bold text-[hsl(var(--alert))]">
                              {e.rounds_unaccounted} round(s) unaccounted.
                            </span>{" "}
                            {e.discrepancy_reason}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              <h3 className="font-heading font-bold mt-5 mb-2">Leave</h3>
              {leaves.map((l) => (
                <div key={l.id} className="flex items-center justify-between border-t border-border py-1 text-xs">
                  <span className="font-mono">{l.from_date} → {l.to_date} · {l.type}</span>
                  <StatusBadge value={l.status} />
                </div>
              ))}
              <div className="grid grid-cols-5 gap-2 mt-2 items-end">
                <div><Label className="text-[10px] uppercase font-bold">From</Label><Input data-testid="leave-from" type="date" className="rounded-none mt-1 h-8" value={leaveForm.from_date} onChange={(e) => setLeaveForm({ ...leaveForm, from_date: e.target.value })} /></div>
                <div><Label className="text-[10px] uppercase font-bold">To</Label><Input data-testid="leave-to" type="date" className="rounded-none mt-1 h-8" value={leaveForm.to_date} onChange={(e) => setLeaveForm({ ...leaveForm, to_date: e.target.value })} /></div>
                <div>
                  <Label className="text-[10px] uppercase font-bold">Type</Label>
                  <Select value={leaveForm.type} onValueChange={(v) => setLeaveForm({ ...leaveForm, type: v })}>
                    <SelectTrigger data-testid="leave-type" className="rounded-none mt-1 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>{(config?.leave_types || ["vacation", "sick", "emergency"]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-[10px] uppercase font-bold">Reason</Label><Input data-testid="leave-reason" className="rounded-none mt-1 h-8" value={leaveForm.reason} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} /></div>
                <Button size="sm" data-testid="leave-add" className="rounded-none" onClick={addLeave} disabled={!leaveForm.from_date || !leaveForm.to_date}>File leave</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
